import { useState, useEffect } from 'react';
import { ToggleSwitch } from '@/components/ui/toggle-switch';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useUpdateCallerID, useUpdateCNAM } from '../../hooks';
import { UserCheck, Info } from 'lucide-react';
import type { DID } from '../../types';

interface CallerIDCNAMTabProps {
  did?: DID | null;
}

export function CallerIDCNAMTab({ did }: CallerIDCNAMTabProps) {
  // Outbound Caller ID state
  const [callerIdEnabled, setCallerIdEnabled] = useState(false);
  const [displayName, setDisplayName] = useState('');
  const updateCallerID = useUpdateCallerID();

  // Inbound CNAM Lookup state
  const [cnamEnabled, setCnamEnabled] = useState(false);
  const updateCNAM = useUpdateCNAM();

  useEffect(() => {
    if (did) {
      setCallerIdEnabled(did.has_caller_id ?? false);
      setDisplayName(did.cnam_name || '');
      setCnamEnabled(did.cnam ?? false);
    }
  }, [did]);

  const handleCallerIdSubmit = () => {
    if (!did) return;
    updateCallerID.mutate({
      tn: did.tn,
      enabled: callerIdEnabled,
      displayName: callerIdEnabled ? displayName : undefined,
    });
  };

  const handleCnamSubmit = () => {
    if (!did) return;
    updateCNAM.mutate({
      tn: did.tn,
      enabled: cnamEnabled,
    });
  };

  const callerIdValid = !callerIdEnabled || (displayName.length >= 1 && displayName.length <= 15);
  const cnamHasChanges = did?.cnam !== cnamEnabled;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="p-3 bg-violet-50 rounded-xl">
          <UserCheck className="h-6 w-6 text-violet-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-slate-900">Caller ID / CNAM</h3>
          <p className="text-sm text-slate-500 mt-1">
            Configure outbound caller ID display and inbound CNAM lookup settings.
          </p>
        </div>
      </div>

      {/* CNAM Storage Warning */}
      {did && !did.cnam_storage_available && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
          <Info className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            <strong>Note:</strong> CNAM Storage is not available for this DID.
          </p>
        </div>
      )}

      {/* Section: Outbound Caller ID */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">
          Outbound Caller ID
        </h4>
        <div className="bg-slate-50 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">Outbound Caller ID</p>
              <p className="text-sm text-slate-500">
                {callerIdEnabled ? 'Name entered will be displayed on outbound calls' : 'System default caller name will be displayed on outbound calls'}
              </p>
            </div>
            <ToggleSwitch
              enabled={callerIdEnabled}
              onChange={setCallerIdEnabled}
            />
          </div>

          {callerIdEnabled && (
            <div className="pt-4 border-t border-slate-200 space-y-3">
              <Input
                label="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value.toUpperCase())}
                placeholder="BUSINESS NAME"
                maxLength={15}
                error={displayName.length > 15 ? 'Maximum 15 characters' : undefined}
              />
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleCallerIdSubmit}
              isLoading={updateCallerID.isPending}
              disabled={!did || !callerIdValid}
              size="sm"
            >
              Save Caller ID
            </Button>
          </div>
        </div>
      </div>

      {/* Section: Inbound CNAM Lookup */}
      <div>
        <h4 className="text-sm font-semibold text-slate-700 uppercase tracking-wider mb-3">
          Inbound CNAM Lookup
        </h4>
        <div className="bg-slate-50 rounded-xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-slate-900">CNAM Lookup</p>
              <p className="text-sm text-slate-500">
                {cnamEnabled ? 'System will look up caller names' : 'System will display provided caller names, but will not perform reverse lookups'}
              </p>
            </div>
            <ToggleSwitch
              enabled={cnamEnabled}
              onChange={setCnamEnabled}
            />
          </div>

          <div className="flex justify-end pt-2">
            <Button
              onClick={handleCnamSubmit}
              isLoading={updateCNAM.isPending}
              disabled={!did || !cnamHasChanges}
              size="sm"
            >
              {cnamHasChanges ? 'Save CNAM' : 'No Changes'}
            </Button>
          </div>
        </div>
      </div>

      {/* Guidelines */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-sm font-medium text-blue-900 mb-2">CNAM Guidelines</p>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>Use uppercase letters for best compatibility</li>
          <li>Changes typically take 24-48 hours to propagate</li>
          <li>Not all carriers display CNAM information</li>
          <li>CNAM lookup is charged per call - check your contract pricing</li>
        </ul>
      </div>
    </div>
  );
}
